<script setup lang="ts">
import { $t } from "@/i18n";
import { useSessionStore } from "@/store/session";
import { useToast } from "primevue/usetoast";
import { useDefaultStore } from "@/store";

definePage({
	name: "LoginPage",
	meta: {
		name: "Login",
	},
});

const email = ref<string | null>(null);
const password = ref<string | null>(null);
const router = useRouter();
const toast = useToast();
const sessionStore = useSessionStore();
const store = useDefaultStore();

async function onClickLogin() {
	if (!email.value) {
		toast.add({
			severity: "error",
			summary: $t("app.auth.error.requiredEmail.title"),
			detail: $t("app.auth.error.requiredEmail.msg"),
			life: 3000,
		});
		return;
	} else if (!store.mailRegex.test(email.value.trim())) {
		toast.add({
			severity: "error",
			summary: $t("app.auth.error.invalidEmail.title"),
			detail: $t("app.auth.error.invalidEmail.msg"),
			life: 3000,
		});
		return;
	}

	if (!password.value || !password.value.trim()) {
		toast.add({
			severity: "error",
			summary: $t("app.auth.error.requiredPassword.title"),
			detail: $t("app.auth.error.requiredPassword.msg"),
			life: 3000,
		});
		return;
	}

	const payload = {
		email: email.value,
		password: password.value,
	};
	try {
		const isSuccess = await sessionStore.setupLogin(payload, false);
		if (isSuccess) {
			toast.add({
				severity: "success",
				summary: $t("app.auth.success.title"),
				detail: $t("app.auth.success.msg"),
				life: 3000,
			});
			await router.push("/");
		}
		console.log(sessionStore.userData);
	} catch (err: any) {
		console.warn(err.message);
	}
}
</script>

<template>
	<div class="login-wrapper">
		<div class="login-form-wrapper">
			<div class="login-form-title">{{ $t("app.auth.header.title") }}</div>
			<div class="login-form-input-wrapper">
				<label
					class="login-form-input-label"
					for="email"
				>
					{{ $t("app.auth.email.label") }}
				</label>
				<InputText
					v-model="email"
					type="email"
					fluid
					@keyup.enter="onClickLogin()"
				/>
			</div>
			<div class="login-form-input-wrapper">
				<label
					class="login-form-input-label"
					for="passwors"
				>
					{{ $t("app.auth.password.label") }}
				</label>
				<Password
					v-model="password"
					toggleMask
					:feedback="false"
					fluid
					@keyup.enter="onClickLogin()"
				/>
			</div>
			<div class="login-button-wrapper">
				<Button
					:label="$t('app.auth.login')"
					@click="onClickLogin()"
					:pt="{
						root: {
							style: 'margin: 0 auto',
						},
					}"
				/>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.login-wrapper {
	min-height: 100vh;
	display: flex;
	justify-content: center;
	align-items: center;

	.login-form-wrapper {
		border: 1px solid $input-border-color;
		width: 490px;
		padding: 20px;

		.login-form-title {
			margin-bottom: 9px;
			min-height: 56px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 30px;
			font-weight: 800;
			line-height: 35.16px;
		}

		.login-form-input-wrapper {
			display: flex;
			flex-direction: column;
			margin-bottom: 15px;

			.login-form-input-label {
				font-size: 12px;
				font-weight: 600;
				line-height: 14.06px;
				color: $admin-text-color-light;
				margin-bottom: 6px;
			}

			.login-form-input-input {
				height: 37px;
				padding: 8px 12px;
				border: 1px solid $input-border-color;
				border-radius: 6px;
			}
		}

		.login-button-wrapper {
			display: flex;
			justify-content: center;
		}
	}
}
</style>
